import { jwtDecode } from "jwt-decode"
import { z } from "zod"

const PW_AUTH_TOKEN_KEY = "auth_token"

const SCHOOL_ID_REGEX = /school:(?<schoolId>\d+)/
const USER_ID_REGEX = /user:(?<userId>\d+)/

function getAuthTokenSchema(schoolId?: number) {
  return z.object({
    aud: z.literal("auth"),
    jti: z.string().uuid(),
    iss: z
      .string()
      .regex(SCHOOL_ID_REGEX)
      .transform((val, ctx) => {
        const parsed = parseInt(val.match(SCHOOL_ID_REGEX)!.groups!.schoolId)
        if (schoolId && parsed !== schoolId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            params: {
              // Somewhat hacky way of inferring the type of custom error.
              mismatched_school_id: schoolId
            },
            message: `Auth token school ID "${parsed}" does not match current school ID "${schoolId}".`
          })
          return z.NEVER
        }
        return parsed
      }),
    sub: z
      .string()
      .regex(USER_ID_REGEX)
      .transform((val) => parseInt(val.match(USER_ID_REGEX)!.groups!.userId)),
    exp: z.number().transform((val, ctx) => {
      const date = new Date(val * 1000)
      const now = new Date()
      if (date.getTime() < now.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Auth token expiration date "${date.toISOString()}" is in the past.`
        })
        return z.NEVER
      }
      return date
    }),
    iat: z.number().transform((val) => new Date(val * 1000)),
    proxier: z.number().optional(),
    // The superuser_session_transfer bool will always be present for superusers
    // whether proxied or not.
    superuser_session_transfer: z.boolean().optional()
  })
}

// Expected auth token schema.
export const authTokenSchema = getAuthTokenSchema()

function _getAuthTokenSchema(schoolId?: number) {
  return schoolId ? getAuthTokenSchema(schoolId) : getAuthTokenSchema()
}

export type AuthTokenInputData = z.input<typeof authTokenSchema>
export type AuthTokenOutputData = z.infer<typeof authTokenSchema>

export type AuthTokenKey = keyof AuthTokenOutputData

type AuthTokenOption = string | null

type TokenOptions = {
  authToken?: AuthTokenOption
  schoolId?: number
}

type OptionalAuthTokenOption = TokenOptions | AuthTokenOption | undefined

// Transform the argument into a TokenOptions object.
function getAuthTokenOptions(arg: OptionalAuthTokenOption): TokenOptions {
  if (arg && typeof arg === "object") {
    return {
      // Fallback to localStorage if no auth token is provided (as a convenience).
      authToken: arg.authToken || getAuthToken(),
      schoolId: arg.schoolId
    }
  }

  return {
    authToken: arg || getAuthToken()
  }
}

function _safeDecodeAuthToken(authToken: AuthTokenOption | undefined) {
  if (!authToken) return null
  try {
    return jwtDecode(authToken)
  } catch (error) {
    return null
  }
}

export const parseAuthToken = (arg?: OptionalAuthTokenOption) => {
  const options = getAuthTokenOptions(arg)

  return _getAuthTokenSchema(options?.schoolId).parse(
    _safeDecodeAuthToken(options?.authToken)
  )
}

export const safeParseAuthToken = (arg?: OptionalAuthTokenOption) => {
  const options = getAuthTokenOptions(arg)

  return _getAuthTokenSchema(options?.schoolId).safeParse(
    _safeDecodeAuthToken(options?.authToken)
  )
}

export const isUserProxied = (authToken?: AuthTokenOption) => {
  const decodedAuthToken = safeParseAuthToken(authToken)
  return decodedAuthToken.success
    ? Boolean(decodedAuthToken.data.proxier)
    : false
}

export const isSuperUserSessionTransfer = (authToken?: AuthTokenOption) => {
  const decodedAuthToken = safeParseAuthToken(authToken)
  return decodedAuthToken.success
    ? Boolean(decodedAuthToken.data.superuser_session_transfer)
    : false
}

// Leave the current page for sign-in, preserving the route to return to.
// No-ops when already on the sign-in page so a missing token cannot loop.
export function redirectToSignIn() {
  const { pathname = "", search = "", hash = "" } = window.location
  if (pathname.startsWith("/auth/sign-in")) return
  const route = pathname + search + hash
  window.location.assign(`/auth/sign-in/?next=${encodeURIComponent(route)}`)
}

// Clear the invalid auth token and perform a hard refresh.
export function clearInvalidToken() {
  const authToken = getAuthToken()
  if (authToken) {
    clearAuthToken()
    // Perform hard refresh. Seems to be the cleanest approach rather than
    // retaining data in cache associated with expired auth token.
    redirectToSignIn()
  }
}

export function getAuthToken() {
  if (typeof localStorage === "undefined") return null
  return localStorage.getItem(PW_AUTH_TOKEN_KEY)
}

export function setAuthToken(token) {
  return localStorage.setItem(PW_AUTH_TOKEN_KEY, token)
}

export function clearAuthToken() {
  return localStorage.removeItem(PW_AUTH_TOKEN_KEY)
}
